import React from 'react';
import { motion } from 'framer-motion';
import {
  Crown,
  Building2,
  Users,
  Calendar,
  Package,
  Plane,
  MapPin,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

export const AdminDashboard = () => {
  const { translations } = useLanguage(); // Access translations from the context
  const userName = localStorage.getItem('userName');

  // Get current time of day in Arabic
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12)
      return translations?.adminDashboard?.morningGreeting || 'Good Morning';
    if (hour < 17)
      return (
        translations?.adminDashboard?.afternoonGreeting || 'Good Afternoon'
      );
    return translations?.adminDashboard?.eveningGreeting || 'Good Evening';
  };

  return (
    <div className="space-y-8">
      {/* Welcome Section with Parallax Effect */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-[#00857B] to-[#00BFB3] rounded-2xl p-8 overflow-hidden min-h-[250px]"
      >
        {/* Animated Background Pattern */}
        <motion.div
          className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/mosque.png')] bg-repeat opacity-5"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {/* Decorative Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full"
            style={{ filter: 'blur(60px)' }}
            animate={{
              x: [-50, 0, -50],
              y: [-50, 0, -50],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full"
            style={{ filter: 'blur(80px)' }}
            animate={{
              x: [50, 0, 50],
              y: [50, 0, 50],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        <div className="relative z-10">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <Crown className="w-16 h-16 text-white/90 mb-6" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-xl text-white/90 mb-2">{getTimeOfDay()}</h2>
            <h1 className="text-4xl font-bold text-white mb-2">
              {userName ||
                translations?.adminDashboard?.defaultUserName ||
                'System Admin'}{' '}
              👋
            </h1>
            <p className="text-white/80 text-lg">
              {translations?.adminDashboard?.welcomeMessage ||
                'Welcome to the Integrated Hajj Package Management System'}
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Featured Locations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Makkah Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="group relative h-[300px] rounded-2xl overflow-hidden"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1565552645632-d725f8bfc19a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
            alt="Makkah"
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-4 right-4 text-white">
            <h3 className="text-2xl font-bold">
              {translations?.adminDashboard?.makkah || 'Makkah'}
            </h3>
            <div className="flex items-center gap-2 text-white/80">
              <MapPin size={16} />
              <span>
                {translations?.adminDashboard?.holyCity || 'The Holy City'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Madinah Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="group relative h-[300px] rounded-2xl overflow-hidden"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
            alt="Madinah"
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-4 right-4 text-white">
            <h3 className="text-2xl font-bold">
              {translations?.adminDashboard?.madinah || 'Madinah'}
            </h3>
            <div className="flex items-center gap-2 text-white/80">
              <MapPin size={16} />
              <span>
                {translations?.adminDashboard?.prophetMosque ||
                  'Prophet’s Mosque'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Mina Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="group relative h-[300px] rounded-2xl overflow-hidden"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
            alt="Mina"
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
          <div className="absolute bottom-4 right-4 text-white">
            <h3 className="text-2xl font-bold">
              {translations?.adminDashboard?.mina || 'Mina'}
            </h3>
            <div className="flex items-center gap-2 text-white/80">
              <MapPin size={16} />
              <span>
                {translations?.adminDashboard?.pilgrimsCamp ||
                  'Pilgrims’ Camps'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        <Link to="/pilgrims">
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-xl bg-blue-50">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <span className="font-medium text-gray-700">
                {translations?.adminDashboard?.managePilgrims ||
                  'Manage Pilgrims'}
              </span>
            </div>
          </motion.div>
        </Link>

        {/* <Link to="/movements">
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-xl bg-purple-50">
                <Calendar className="w-8 h-8 text-purple-500" />
              </div>
              <span className="font-medium text-gray-700">
                {translations?.adminDashboard?.movementSchedule ||
                  'Movement Schedule'}
              </span>
            </div>
          </motion.div>
        </Link> */}

        <Link to="/flights">
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-xl bg-green-50">
                <Plane className="w-8 h-8 text-green-500" />
              </div>
              <span className="font-medium text-gray-700">
                {translations?.adminDashboard?.flights || 'Flights'}
              </span>
            </div>
          </motion.div>
        </Link>

        <Link to="/accommodation">
          <motion.div
            whileHover={{ y: -5, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-xl bg-yellow-50">
                <Building2 className="w-8 h-8 text-yellow-500" />
              </div>
              <span className="font-medium text-gray-700">
                {translations?.adminDashboard?.accommodation || 'Accommodation'}
              </span>
            </div>
          </motion.div>
        </Link>
      </motion.div>
    </div>
  );
};
